'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { i18n as I18n, TFunction } from 'i18next';
import { Hash } from 'lucide-react';
import { FilterGroup } from '../FilterGroup';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import {
  EuidField,
  LeiCodeField,
  RegisterCourtField,
  RegisterIdField,
  RegisterTypeField,
  UstIdnrField,
} from './RegisterFilterGroupParts';

export interface RegisterFilterGroupProps {
  t: TFunction<'translation', undefined>;
  registerCount: number;
  clearRegister: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | null;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  handleFormSubmit: () => void;
  registerCourtOpen: boolean;
  setRegisterCourtOpen: Dispatch<SetStateAction<boolean>>;
  setRegisterCourtSearch: Dispatch<SetStateAction<string>>;
  i18n: I18n;
  registerCourtSearch: string;
  courtList: string[];
  facetCount: (facet: string, value: string) => number | undefined;
  registerTypeOpen: boolean;
  setRegisterTypeOpen: Dispatch<SetStateAction<boolean>>;
  registerTypeOptions: { value: string; label: string }[];
  lockOf: (control: string) => 'user' | null;
}

export function RegisterFilterGroup(props: RegisterFilterGroupProps) {
  const {
    t,
    registerCount,
    clearRegister,
    openFilterGroup,
    handleGroupOpenChange,
    groupLock,
  } = props;
  return (
    <FilterGroup
      id="register"
      icon={Hash}
      label={t('search.sidebar.groups.register', {
        defaultValue: 'Register & identifiers',
      })}
      activeCount={registerCount}
      onClear={clearRegister}
      open={openFilterGroup === 'register'}
      onOpenChange={handleGroupOpenChange('register')}
      lock={groupLock('register')}
    >
      <EuidField {...props} />
      <RegisterIdField {...props} />
      <RegisterCourtField {...props} />
      <RegisterTypeField {...props} />
      <LeiCodeField {...props} />
      <UstIdnrField {...props} />
      {/* A new identifier filter: a field component in
          RegisterFilterGroupParts.tsx reading staged.<key> / set.<key>
          (free-text inputs use submitOnEnter), rendered here in sidebar
          order. */}
    </FilterGroup>
  );
}
